import { Accordion, Badge, Box, Card, useOpenClose } from '@palmetto/palmetto-components';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import {
  AccountRequirementStatus,
  Applicant,
  ApplicantType,
  ApplicationDocumentWithAdditionalApplicantData,
  FinancingDecision,
  MilestoneRequirement,
} from 'types';

import { useGetAccountQuery } from '../../services/accounts';
import { useGetApplicationsQuery } from '../../services/applications';
import { StatusIcon } from '../StatusIcon';

import { CreditApplications } from './CreditApplications';
import { IdentityVerification } from './IdentityVerification';
import { TermsAndConditions } from './TermsAndConditions';
import { TitleVerification } from './TitleVerification';

export function Underwriting() {
  const { id } = useParams<{ id: any }>();
  const { data: account } = useGetAccountQuery(id);
  const programType = account?.programType;
  const { data: applications = [] } = useGetApplicationsQuery({ accountId: id });

  const statuses = useMemo(
    () =>
      account?.milestones?.reduce(
        (acc: any, milestone: any) => {
          function findReqStatus(requirements: any, type: MilestoneRequirement) {
            return requirements?.find((req: any) => req.type === type)?.status;
          }
          const underwritingReq = milestone.requirements.find((req: any) => req.type === 'underwriting');
          if (underwritingReq) {
            acc.underwriting = underwritingReq.status;
          }
          if (!acc.title) {
            acc.title = findReqStatus(
              underwritingReq ? underwritingReq.requirements : milestone?.requirements,
              MilestoneRequirement.titleApproved,
            );
          }
          if (!acc.identity) {
            acc.identity = findReqStatus(
              underwritingReq ? underwritingReq.requirements : milestone?.requirements,
              MilestoneRequirement.identityVerified,
            );
          }
          if (!acc.termsAndConditions) {
            acc.termsAndConditions = findReqStatus(
              underwritingReq ? underwritingReq.requirements : milestone?.requirements,
              MilestoneRequirement.acceptTermsAndConditions,
            );
          }

          if (!acc.credit) {
            acc.credit = findReqStatus(
              underwritingReq ? underwritingReq.requirements : milestone?.requirements,
              MilestoneRequirement.creditApproved,
            );
          }
          return acc;
        },
        {} as Record<any, any>,
      ),
    [account],
  );

  const approvedApplications = applications.filter(
    (application: ApplicationDocumentWithAdditionalApplicantData) =>
      application.status === FinancingDecision.approved ||
      application.status === FinancingDecision.approvedWithStipulations,
  );

  const firstApprovedApplication = approvedApplications[0];

  const allRequirementsApproved = useMemo(() => {
    const presentRequirementStatuses = [
      statuses?.credit,
      statuses?.identity,
      statuses?.title,
      statuses?.termsAndConditions,
    ].filter((requirementStatus): requirementStatus is AccountRequirementStatus => requirementStatus !== undefined);

    return (
      presentRequirementStatuses.length > 0 &&
      presentRequirementStatuses.every(
        (requirementStatus: AccountRequirementStatus) => requirementStatus === AccountRequirementStatus.completed,
      )
    );
  }, [statuses]);

  const { isOpen: isSectionOpen, handleToggle: handleToggleSection } = useOpenClose({
    defaultIsOpen: !allRequirementsApproved,
  });

  return (
    <Box>
      <Card>
        <Accordion.Panel isOpen={isSectionOpen} borderWidth="0">
          <Accordion.PanelSummary
            isDetailsOpen={isSectionOpen}
            onToggle={allRequirementsApproved ? handleToggleSection : undefined}
            padding="lg"
            hasCaret={allRequirementsApproved ? 'right' : false}
          >
            <Box childGap="lg" alignItems="center" direction="row" justifyContent="space-between" flex="auto">
              <Box direction="row" alignItems="center">
                <Box padding="0 xs 0 0">
                  <StatusIcon
                    status={allRequirementsApproved ? AccountRequirementStatus.completed : statuses?.underwriting}
                    size="md"
                  />
                </Box>
                <Box as="h3" fontWeight="medium" fontSize="md">
                  Underwriting
                </Box>
              </Box>
              {allRequirementsApproved && <Badge message="ALL APPROVED" variant="success" />}
            </Box>
          </Accordion.PanelSummary>

          <Accordion.PanelDetails padding="0" borderWidth="0">
            {statuses?.credit && (
              <CreditApplications
                status={statuses?.credit}
                applications={applications}
                firstApprovedApplication={firstApprovedApplication}
              />
            )}
            {statuses?.termsAndConditions && (
              <TermsAndConditions
                status={statuses?.termsAndConditions}
                programType={programType}
                primaryApplicant={account?.applicants?.find(
                  (applicant: Applicant) => applicant?.type === ApplicantType.primary,
                )}
              />
            )}
            <IdentityVerification
              status={statuses?.identity}
              creditStatus={statuses?.credit}
              programType={account?.programType}
            />
            {statuses?.title && (
              <TitleVerification
                status={statuses?.title}
                application={firstApprovedApplication}
                creditStatus={statuses?.credit}
                programType={programType}
              />
            )}
          </Accordion.PanelDetails>
        </Accordion.Panel>
      </Card>
    </Box>
  );
}
